import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Modal } from "app/components/Modal";
import { DropdownIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  MANUFACTURER_OPTIONS,
  SuperModelSelectOption,
} from "../../data";
import {
  selectIsAddModalOpen,
  selectSuperModelForm,
  selectSuperModelSaving,
} from "../../redux/selector";
import { superModelActions } from "../../redux/slice";
import { SuperModelForm } from "../../types";
import { useStyles } from "./styles";

type FieldConfig = {
  key: keyof SuperModelForm;
  label: string;
  type: "select" | "text";
  options?: SuperModelSelectOption[];
};

const FIELDS: FieldConfig[] = [
  { key: "name", label: "NAME", type: "text" },
  {
    key: "manufacturer",
    label: "MANUFACTURER",
    type: "select",
    options: MANUFACTURER_OPTIONS,
  },
  { key: "priority", label: "PRIORITY", type: "text" },
];

const P = "SUPER_MODEL.MODAL";

const AddSuperModelModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectSuperModelForm);
  const saving = useSelector(selectSuperModelSaving);

  const onClose = () => {
    if (saving) return;
    dispatch(superModelActions.closeAddModal());
  };

  const setField = (name: keyof SuperModelForm, value: string) =>
    dispatch(superModelActions.setFormField({ name, value }));

  const placeholder = (label: string, type: "select" | "text") =>
    t(`${P}.PLACEHOLDERS.${type === "select" ? "SELECT" : "ENTER"}_${label}`);

  const optionLabel = (option: SuperModelSelectOption) =>
    option.labelKey ? t(option.labelKey) : option.label || option.value;

  const isValid =
    Boolean(form.name.trim()) &&
    Boolean(form.manufacturer.trim()) &&
    Boolean(form.priority.trim());

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>{t(`${P}.ADD_TITLE`)}</Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {FIELDS.map(({ key, label, type, options = [] }) => (
            <Box key={key} className={styles.field}>
              <Typography className={styles.label}>
                {t(`${P}.FIELDS.${label}`)}
              </Typography>
              <TextField
                className={styles.input}
                fullWidth
                select={type === "select"}
                variant="outlined"
                placeholder={placeholder(label, type)}
                value={form[key]}
                disabled={saving}
                InputProps={{ notched: false }}
                SelectProps={
                  type === "select"
                    ? {
                        displayEmpty: true,
                        IconComponent: DropdownIcon,
                        renderValue: (value) => {
                          const selected = options.find(
                            (option) => option.value === value
                          );
                          return selected
                            ? optionLabel(selected)
                            : placeholder(label, "select");
                        },
                      }
                    : undefined
                }
                onChange={(event) => setField(key, event.target.value)}
              >
                {type === "select"
                  ? [
                      <MenuItem key="empty" value="" disabled>
                        {placeholder(label, "select")}
                      </MenuItem>,
                      ...options.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {optionLabel(option)}
                        </MenuItem>
                      )),
                    ]
                  : undefined}
              </TextField>
            </Box>
          ))}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid || saving}
            onClick={() => dispatch(superModelActions.addSuperModel())}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddSuperModelModal;
